/**
 * VerifyOtp - Documentary Editorial aesthetic
 *
 * OTP / verification code entry screen shown after sign-up.
 * Matches the SignUpFormRedesign styling.
 */

import { Form, useFormikContext } from "formik";
import { memo, useState } from "react";
import { FormikInput } from "components";
import AuthPanelRedesign from "components/AuthPanel/AuthPanelRedesign";
import { FIELD_NAMES, OTP_LENGTH } from "./VerifyOtp.constants";

interface ValuesType {
  token: string;
}

function EditorialButton({
  children,
  disabled,
  type = "button",
}: {
  children: React.ReactNode;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
}) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      type={type}
      disabled={disabled}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        relative w-full py-3.5 px-6 rounded-lg font-medium text-base
        transition-all duration-300 ease-out
        ${disabled
          ? 'bg-stone-300 text-stone-500 cursor-not-allowed'
          : 'text-white cursor-pointer'
        }
      `}
      style={{
        backgroundColor: disabled ? undefined : (isHovered ? '#A13508' : '#C2410C'),
        fontFamily: "'Source Sans 3', sans-serif",
        boxShadow: disabled ? 'none' : (
          isHovered
            ? '0 10px 30px -5px rgba(194, 65, 12, 0.4)'
            : '0 4px 15px -3px rgba(194, 65, 12, 0.3)'
        ),
        transform: isHovered && !disabled ? 'translateY(-1px)' : 'translateY(0)',
      }}
    >
      {children}
    </button>
  );
}

function EditorialInputWrapper({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <label
        className="block text-sm font-medium tracking-wide"
        style={{
          color: '#44403C',
          fontFamily: "'Source Sans 3', sans-serif",
        }}
      >
        {label}
      </label>
      <div className="relative">{children}</div>
    </div>
  );
}

interface VerifyOtpComponentProps {
  email: string;
}

function VerifyOtpComponent({ email }: VerifyOtpComponentProps) {
  const { isSubmitting } = useFormikContext<ValuesType>();

  return (
    <AuthPanelRedesign variant="signup">
      <h1
        className="mt-6 text-3xl lg:text-4xl leading-tight"
        style={{
          color: '#1C1917',
          fontFamily: "'Fraunces', serif",
          fontWeight: 400,
        }}
      >
        Check your email
      </h1>
      <p
        className="mt-3 text-base"
        style={{
          color: '#78716C',
          fontFamily: "'Source Sans 3', sans-serif",
        }}
      >
        We sent a {OTP_LENGTH}-digit verification code to{' '}
        <span style={{ color: '#1C1917', fontWeight: 500 }}>
          {email || 'your email'}
        </span>
        . Enter it below to finish setting up your account.
      </p>

      <Form className="mt-8 space-y-5">
        <EditorialInputWrapper label="Verification code">
          <FormikInput
            autoComplete="one-time-code"
            inputMode="numeric"
            maxLength={OTP_LENGTH}
            name={FIELD_NAMES.TOKEN}
            placeholder="123456"
            className="!rounded-[6px] !border-0 !bg-white !px-4 !py-3 focus:!border-[#C2410C] focus:!ring-[#C2410C]/20 !transition-all !w-[calc(100%-60px)] !tracking-[0.4em] !text-lg"
            style={{ fontFamily: "'Source Sans 3', sans-serif" }}
          />
        </EditorialInputWrapper>

        <p
          className="text-xs"
          style={{
            color: '#A8A29E',
            fontFamily: "'Source Sans 3', sans-serif",
          }}
        >
          The code expires in a few minutes. Can't find it? Check your spam folder.
        </p>

        <div className="pt-2">
          <EditorialButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Verifying...' : 'Verify and continue'}
          </EditorialButton>
        </div>
      </Form>
    </AuthPanelRedesign>
  );
}

export default memo(VerifyOtpComponent);
